import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { BenchmarkIcon } from "@images/svgIcons/benchmarkIcon"
import { BoxIcon } from "@images/svgIcons/boxIcon"
import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { ProfileIcon } from "@images/svgIcons/profileIcon"
import { ProSmallIcon } from "@images/svgIcons/ProSmallIcon"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import {
  ExploreBenchmarkItemEntity,
  ExploreBoxItemEntity,
  ExploreProfileItemEntity,
  ExploreProgramBundleItemEntity,
  ExploreProgramItemEntity,
  ExploreProProgramItemEntity,
} from "@modules/explore/entities/exploreEntity"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramType } from "@modules/programs/types/programTypes"
import { BoxesIcon, Check } from "lucide-react"
import { useMemo } from "react"

type ExploreCardHeaderProps = {
  item:
    | ExploreBenchmarkItemEntity
    | ExploreProgramItemEntity
    | ExploreProgramBundleItemEntity
    | ExploreProProgramItemEntity
    | ExploreBoxItemEntity
    | ExploreProfileItemEntity
  className?: string
} & React.ComponentProps<"div">

export function ExploreCardHeader({ item, className, ...props }: ExploreCardHeaderProps) {
  const language = useLanguage()

  const itemIcon = useMemo(() => {
    switch (item._type) {
      case ExploreFilter.PROGRAM:
        if (item.programType === ProgramType.VIRTUAL) {
          return (
            <InfinityIcon color="currentColor" className="mr-2 shrink-0 text-textOnColor-body" width={20} height={20} />
          )
        } else
          return (
            <OnOffIcon color="currentColor" className="mr-2 shrink-0 text-textOnColor-body" width={20} height={20} />
          )
      case ExploreFilter.PROGRAM_BUNDLE:
        return <BoxesIcon color="currentColor" width={20} height={20} className="ml-1 shrink-0 text-textOnColor-body" />
      case ExploreFilter.PROGRAM_PRO:
        return (
          <ProSmallIcon color="currentColor" width={20} height={20} className="ml-1 shrink-0 text-textOnColor-body" />
        )
      case ExploreFilter.BOX:
        return (
          <BoxIcon color="currentColor" width={20} height={20} className="mr-2 shrink-0 text-textPrimary-on-color" />
        )
      case ExploreFilter.BENCHMARK:
        return (
          <BenchmarkIcon color="currentColor" width={20} height={20} className="mr-2 shrink-0 text-textOnColor-body" />
        )
      case ExploreFilter.PROFILE:
        return (
          <ProfileIcon color="currentColor" width={20} height={20} className="mr-2 shrink-0 text-textOnColor-body" />
        )
    }
  }, [item])

  const itemType = useMemo(() => {
    switch (item._type) {
      case ExploreFilter.PROGRAM:
        return word("global.program.name")
      case ExploreFilter.PROGRAM_PRO:
        return word("global.program.name")
      case ExploreFilter.PROGRAM_BUNDLE:
        return `${word("programBundle.info.labelShort")} x${item instanceof ExploreProgramBundleItemEntity ? item.nbPrograms : 0}`
      case ExploreFilter.BOX:
        return word("global.box.name")
      case ExploreFilter.BENCHMARK:
        return word("global.benchmark.name")
      case ExploreFilter.PROFILE:
        return word("global.profile.name")
    }
  }, [item])

  return (
    <div className={cn("z-2 flex flex-row justify-between p-4", className)} {...props}>
      <div className="flex items-center gap-1 rounded-full bg-gray-800/60 px-3 py-2">
        {itemIcon}
        <Text fontStyle="LS" className="text-textOnColor-body">
          {itemType}
        </Text>
      </div>
      {"price" in item && item.price?.value !== undefined && (
        <div className="flex items-center gap-1 rounded-full bg-gray-800/60 px-3 py-2">
          {item.subscribed && <Check size={12} />}
          <Text fontStyle="LS" className="text-textOnColor-body">
            {`${formatCurrency(item.price.value, item.price.currency, language)} ${item.programType === ProgramType.VIRTUAL ? word("program.price.priceSlashPerMonth") : ""}`}
          </Text>
        </div>
      )}
    </div>
  )
}
